import React, { FC } from 'react';
import { Box, keyframes, LinearProgress, linearProgressClasses, Typography } from '@mui/material';
import Container from '@/components/Container';
import type { SeasonalLoaderProps } from '../../SeasonalTheme';
import { ChristmasTree } from './ChristmasTree';
import { Snowflake } from './Snowflake';
import { ChristmasColors, CHRISTMAS_GRADIENT } from './christmasColors';

const bob = keyframes`
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-6%) rotate(2deg); }
`;

const drift = keyframes`
  0%   { transform: translate3d(0, -120%, 0) rotate(0deg); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translate3d(var(--sway), 160%, 0) rotate(180deg); opacity: 0; }
`;

const FLAKES = [
  { left: '6%', delay: 0, duration: 3.6, sway: '14px', color: ChristmasColors.SNOW_EDGE },
  { left: '24%', delay: 1.4, duration: 4.2, sway: '-18px', color: ChristmasColors.SNOW_BLUE },
  { left: '48%', delay: 2.2, duration: 3.9, sway: '10px', color: ChristmasColors.SNOW_EDGE },
  { left: '70%', delay: 0.7, duration: 4.5, sway: '-12px', color: ChristmasColors.SNOW_BLUE },
  { left: '88%', delay: 1.9, duration: 3.4, sway: '16px', color: ChristmasColors.SNOW_EDGE },
];

/** Christmas variant of `components/Loader`: a bobbing tree, drifting snow and a Christmas-coloured progress bar. */
export const ChristmasLoader: FC<SeasonalLoaderProps> = ({
  loadingText,
  width = '300px',
  height = '100px',
  progressProps,
}) => {
  const treeSize = Math.max(28, Math.min(80, Math.round(parseInt(height, 10) * 0.7) || 70));
  return (
    <Container column fullHeight>
      <Box
        data-testid='ChristmasLoader'
        sx={{
          position: 'relative',
          width,
          maxWidth: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
        {FLAKES.map((flake) => (
          // Keyframes must be referenced from `sx` (Emotion) to be injected; per-flake values go in `style`.
          <Box
            key={flake.left}
            sx={{
              position: 'absolute',
              top: 0,
              lineHeight: 0,
              animation: `${drift} linear infinite both`,
              '@media (prefers-reduced-motion: reduce)': { display: 'none' },
            }}
            style={
              {
                left: flake.left,
                '--sway': flake.sway,
                animationDuration: `${flake.duration}s`,
                animationDelay: `${flake.delay}s`,
              } as React.CSSProperties
            }>
            <Snowflake size={Math.round(treeSize * 0.28)} color={flake.color} />
          </Box>
        ))}
        <Box
          sx={{
            display: 'inline-flex',
            animation: `${bob} 1.8s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}>
          <ChristmasTree size={treeSize} />
        </Box>
      </Box>
      <LinearProgress
        variant='indeterminate'
        {...progressProps}
        sx={{
          width: '100%',
          height: 6,
          borderRadius: 999,
          backgroundColor: 'rgba(47, 122, 58, 0.15)',
          // Only recolour the bar. Never touch `animation` here: MUI's indeterminate movement lives on the same
          // element and overriding it collapses the bar to zero width.
          [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 999,
            backgroundImage: CHRISTMAS_GRADIENT,
          },
          ...(progressProps?.sx as object),
        }}
      />
      {loadingText && <Typography>{loadingText}</Typography>}
    </Container>
  );
};

export default ChristmasLoader;
